/**
 * Retries a given asynchronous function on failure using exponential backoff.
 *
 * @param {Function} fn - The asynchronous function to retry.
 * @param {number} retries - Number of retry attempts before failing.
 * @param {number} delay - Initial delay between retries in milliseconds.
 * @param {string} operationName - Optional name for the operation (for logging purposes).
 * @returns {Promise<any>} - Resolves with the asynchronous function's result, or rejects if all retries fail.
 */
export const retry = async <T>(fn: () => Promise<T>, retries: number = 3, delay: number = 1000, operationName: string = 'Operation'): Promise<T> => {
  const maxRetries = retries; // Store initial value for logging
  try {
    const result = await fn();
    return result;
  } catch (error) {
    if (retries > 0) {
      const currentRetry = maxRetries - retries + 1;
      const nextDelay = delay * 2;

      console.log(`[${operationName}] Retry Attempt: (${currentRetry})`);
      if (error instanceof Error) {
        console.log(`[${operationName}] Error: ${error.message}`);
      }

      await new Promise((resolve) => setTimeout(resolve, delay));
      return retry(fn, retries - 1, nextDelay, operationName);
    } else {
      console.error(`[${operationName}] All retry attempts failed`);
      throw error;
    }
  }
};
